import { useTranslation } from 'react-i18next';
import { Feedback } from '../feedback';

/**
 * Each member is its key in the `errors` namespace, which also carries the
 * codes services send, so a response code maps to a message with no
 * table in between.
 */
export enum ErrorKey {
  InputIdentifierValidation = 'InputIdentifierValidation',
  InputNameValidation = 'InputNameValidation',
  InputDescriptionValidation = 'InputDescriptionValidation',
  EmailValidation = 'EmailValidation',
  UuidValidation = 'UuidValidation',
  UrlValidation = 'UrlValidation',
  MfaValidation = 'MfaValidation',
  DomainValidation = 'DomainValidation',

  // Error Response Keys
  InvalidMFAToken = 'InvalidMFAToken',
  TokenExpired = 'TokenExpired',
  ExceededMaxFailedAttemptsThreshold = 'ExceededMaxFailedAttemptsThreshold',
  MissingTenantIds = 'MissingTenantIds',
  EmailAlreadyExists = 'EmailAlreadyExists',
  InvalidBody = 'InvalidBody',

  UnknownError = 'UnknownError',
}

export interface ErrorMessageProps {
  /**
   * A predefined error key from the ErrorKey enum. Displays a predefined error message
   */
  errorKey?: ErrorKey;
  /**
   * A custom error message to override the predefined error message
   */
  customErrorMessage?: string;
  /**
   * Custom CSS class names for styling the error message.
   */
  customClassName?: string;
  /**
   * The type of error message to display. This will be used to determine the styling of the error message.
   */
  errorType?: 'form' | 'feedback';
  /**
   * Props to pass to the Feedback component if errorType is 'feedback'
   */
  feedbackProps?: React.ComponentProps<typeof Feedback>;
}

export const ErrorMessage = ({
  errorKey,
  customErrorMessage,
  customClassName,
  errorType = 'form',
  feedbackProps,
}: ErrorMessageProps) => {
  const { t } = useTranslation('errors');
  let errorMessage = t(ErrorKey.UnknownError);

  const className = customClassName
    ? `${customClassName} text-sm text-error-base`
    : 'text-sm text-error-base ';

  if (customErrorMessage) {
    errorMessage = customErrorMessage;
  } else if (errorKey) {
    errorMessage = t(errorKey);
  }

  switch (errorType) {
    case 'feedback':
      return (
        <Feedback {...feedbackProps}>
          <p>{errorMessage}</p>
        </Feedback>
      );
    case 'form':
    default:
      return <div className={className}>{errorMessage}</div>;
  }
};
